import { type ReactNode } from "react";

import type { IBuiHeaderProps } from "@btnet/blueprint-components/BuiHeader";

/**
 * An object-shaped route target for a header link.
 *
 * Assignable to react-router-dom's
 * `LinkProps["to"]` — which is where these values ultimately flow, via BTLink/BuiLink.
 * Deliberately NOT aliased to history's `LocationDescriptorObject`: that makes
 * `pathname` optional and adds `hash`/`key`, which would widen these props rather
 * than swap the type one-for-one.
 */
export interface IBTItemHeaderLocation<S = unknown> {
    pathname: string;
    search?: string;
    state?: S;
}

/**
 * The core icon actions for BTItemHeader. `mappedActionIconDefaults` in
 * BTItemHeader.tsx supplies their defaults.
 *
 * Unrelated to BTListHeader's `TBTListHeaderIconVariant`, which is a different
 * set backed by its own defaults map — the two share only "print".
 */
export type TBTItemHeaderCoreIcon = "edit" | "copy" | "delete" | "print" | "share" | "download";

/**
 * Overflow padding (px) reserved per visible action. The Fluent overflow manager
 * measures item boxes but not the horizontal spacing between them, so it under-counts
 * total width; reserving this much per action compensates (assumes ~16px of spacing
 * per item, 8px on each side). Mirrors BTListHeader's BT_LIST_HEADER_OVERFLOW_PADDING.
 */
export const BT_ITEM_HEADER_OVERFLOW_PADDING = 16;

/**
 * A secondary action paired with explicit overflow behavior. Pass this (instead of
 * a bare `ReactNode`) when an action must remain reachable under width pressure —
 * typically the page's canonical Cancel/dismiss affordance.
 */
export interface IBTItemHeaderSecondaryAction {
    /** The action element to render (typically a `BTButton`). */
    node: ReactNode;
    /**
     * When true, the action is always visible and never collapses into the overflow
     * menu. When false/omitted it rolls up under width pressure like any other
     * contextual action.
     * @default false
     */
    pinned?: boolean;
}

/**
 * A text-only action that always lives inside the overflow ("More") menu, never as an inline
 * button. Use for workflow actions a design keeps tucked away at every viewport width (e.g.
 * Approve/Decline/Copy/Delete/Save to catalog). They share the single overflow menu with any
 * icon actions that roll up under width pressure, so the header keeps exactly one "More".
 * Unlike `iconActions` with `inMenu`, these need no design-system icon key — the label is
 * supplied directly and no icon is shown in the menu.
 */
export interface IBTItemHeaderMenuAction {
    /** The menu item's visible label (also its accessible name). */
    label: string;
    /** Stable test id for the menu item's button. */
    testid: string;
    /** Invoked when the item is chosen. */
    onClick: () => void;
    /** When true, the item is rendered disabled. */
    disabled?: boolean;
    /** When true, the item is not rendered at all. */
    isHidden?: boolean;
    /**
     * When true, the item is styled as a destructive action (e.g. Delete/Void).
     * @default false
     */
    danger?: boolean;
    /**
     * When true, a divider is rendered above this item to separate it from the group before it
     * (e.g. to set a destructive Delete apart from the neutral actions above it).
     * @default false
     */
    dividerBefore?: boolean;
}

/**
 * A menu action supplied as an already-rendered node (e.g. a `BTButton`) rather than a text
 * descriptor. Use when the action needs behavior a plain descriptor can't express — disabled
 * gating, hotkeys, confirm dialogs — while still living only in the overflow ("More") menu.
 */
export interface IBTItemHeaderMenuActionNode {
    /** The rendered action to place inside the menu item. */
    node: ReactNode;
    /** Stable id / test id for the menu item wrapper. */
    testid: string;
    /** When true, the item is not rendered at all. */
    isHidden?: boolean;
    /** When true, a divider is rendered above this item. */
    dividerBefore?: boolean;
}

/** A menu action: either a text descriptor or a pre-rendered node. */
export type TBTItemHeaderMenuAction = IBTItemHeaderMenuAction | IBTItemHeaderMenuActionNode;

/**
 * Props for the BTHeaderActionButtons component.
 */
export interface IBTItemHeaderActionButtonsProps {
    /**
     * Optional back link URL or location descriptor. If provided, a back link will be displayed in the header.
     * Can be a string URL or an IBTItemHeaderLocation object.
     * @example
     * '/previous-page'
     * @example
     * { pathname: '/previous-page', state: { fromDashboard: true } }
     */
    backLink?: string | IBTItemHeaderLocation;

    /**
     * Optional onClick handler for the back link. If provided, this function will be called when the back link is clicked.
     * @returns void
     */
    backLinkOnClick?: () => void;

    /**
     * Optional onClick handler for a left-anchored close (X) affordance. Use for
     * panel/modal contexts where the item is dismissed rather than navigated back
     * from. When provided, the close X takes the place of the back arrow in the
     * left slot — the two are mutually exclusive (X wins), matching the item-header
     * design guide. Like the back link, it is not rendered inside a webview.
     * @returns void
     */
    closeOnClick?: () => void;

    /**
     * Optional additional class name(s) for custom styling of the action buttons container.
     */
    className?: string;

    /**
     * Optional array of icon action configurations for the header.
     *
     * Actions flagged `leading` render as a far-left cluster (always visible),
     * with everything else right-aligned alongside the primary/secondary actions
     * — used by split-view detail panels whose window chrome (close/expand) sits
     * on the left, opposite Save/Cancel. See the warranty panel.
     */
    iconActions?: TBTItemHeaderIcon[];

    /**
     * Optional actions that always render inside the overflow ("More") menu, never as inline
     * buttons. Each is either a text descriptor or a pre-rendered node (for actions needing
     * disabled gating / hotkeys / confirms). They share the single overflow menu with any icon
     * actions that roll up under width pressure, so the header keeps exactly one "More".
     */
    menuActions?: TBTItemHeaderMenuAction[];

    menuDisabled?: boolean;

    menuTooltip?: ReactNode;

    /**
     * Optional primary action element to be displayed in the header.
     */
    primaryAction?: ReactNode;

    /**
     * Optional secondary action element(s) to be displayed in the BTItemHeader.
     * Each item may be either:
     * - a raw `ReactNode`, which rolls up into the overflow menu under width
     *   pressure (the default — matches BTListHeader), or
     * - an `IBTItemHeaderSecondaryAction` config (`{ node, pinned }`) to opt a
     *   specific action into staying pinned and always visible — use this for the
     *   canonical Cancel/dismiss affordance.
     *
     * Pinning is explicit and per-item: a lone secondary action is NOT auto-pinned,
     * so single-button consumers (e.g. a report's Export) keep collapsing as before.
     */
    secondaryActions?: (ReactNode | IBTItemHeaderSecondaryAction)[];

    /**
     * The test ID for the header action buttons component.
     */
    testid: string;
}

export interface IBTItemHeaderCommentButton {
    /** Whether the entity has any comments. Controls the filled vs outlined icon state. */
    hasComments: boolean;
    /** Whether there are unread comments. When true, a visual indicator is shown on the comment icon. */
    hasUnreadComments: boolean;
    /** Whether the comment drawer is currently open. Controls the selected/active state of the button. */
    isCommentDrawerOpen: boolean;
    /** Callback invoked when the comment button is clicked. Typically toggles the comment drawer. */
    onCommentsClick: () => void;
    /** When true, the button is rendered disabled (e.g. before the entity has been saved). */
    disabled?: boolean;
    /** Overrides the default "Comments" popover content. Useful for explaining a disabled state. */
    tooltip?: ReactNode;
}

type TBTItemHeaderIconVisibility =
    | {
          /** When true, this action is always shown as a standalone button and never collapses into the overflow menu. */
          pinned?: boolean;
          priority?: never;
      }
    | {
          /** Numeric priority controlling the order in which actions collapse into the overflow menu. Higher values collapse later. */
          pinned?: never;
          priority?: number;
      };

type TBTItemHeaderIconAction =
    | {
          to?: never;
          /**
           * Callback function triggered when the icon button is clicked.
           * @returns void
           */
          onClick: () => Promise<void> | void;
      }
    | {
          /** The location to navigate to when the icon button is clicked.
           * Can be a string URL or an IBTItemHeaderLocation object. */
          to: IBTItemHeaderLocation | string;
          onClick?: never;
      }
    | {
          /**
           * Action with neither `onClick` nor `to`. Use this when the icon button has no direct
           * activation behavior of its own — for example, it acts purely as a `popoverContent`
           * trigger, is rendered as a non-interactive indicator, or is a `customComponent` that
           * supplies its own click/navigation behavior internally. If you want the button itself
           * to fire a callback or navigate, use one of the other variants instead.
           */
          to?: never;
          onClick?: never;
      };

/** A standard icon action that uses a mapped icon from the design system. */
export type TBTItemHeaderMappedIcon = {
    ariaLabel?: string;
    type?: never;
    /** The icon to display. Must be a TBTItemHeaderCoreIcon, "collapse", "help", "activity", "history", "closeX", "statusChange", "saveToCatalog", "shareLink", or "copyToTemplate". */
    icon:
        | TBTItemHeaderCoreIcon
        | "collapse"
        | "help"
        | "activity"
        | "history"
        | "closeX"
        | "statusChange"
        | "saveToCatalog"
        | "shareLink"
        | "copyToTemplate";
    id?: never;
    customComponent?: never;
    /** Content to render inside a BTPopover anchored to this button. */
    popoverContent?: ReactNode;
    /** Controls the width of the popover. Defaults to "narrow". */
    popoverWidthType?: "narrow" | "wide" | "unrestricted";
    tooltip?: ReactNode;
    /** When true, the action's button is rendered disabled (and its onClick suppressed). */
    disabled?: boolean;
    /** When true, this action only appears in the overflow menu and never as a standalone button. */
    inMenu?: boolean;
    /** When true, this action only appears in the overflow menu on md screens (480px) and never as a standalone button. */
    inMenuMdBreakpoint?: boolean;
    /**
     * When true, this action renders in a far-left cluster (always visible,
     * never collapses into the overflow menu) instead of with the other actions
     * on the right. Use for panel window chrome such as close/expand.
     * @default false
     */
    leading?: boolean;
    /**
     * If true, header icon will be hidden.
     * @default false
     */
    isHidden?: boolean;
} & TBTItemHeaderIconVisibility &
    TBTItemHeaderIconAction;

/** A custom component action that renders caller-provided JSX instead of a mapped icon. */
type TBTItemHeaderCustomComponent = {
    ariaLabel?: string;
    type: "customComponent";
    /** A unique string identifier for this action, used as the React key and overflow item id. */
    id: string;
    icon?: never;
    /** Custom component to render instead of the default button.
     * When provided, all other rendering props (popoverContent, etc.) are ignored.
     * It is assumed that the customComponent handles to/onClick behavior. */
    customComponent: ReactNode;
    /**
     * The form this action takes once it rolls up into the overflow ("More") menu. Omit it and
     * the inline `customComponent` is reused verbatim — which for an icon-only button means an
     * unlabeled icon in a text menu. Supply a labeled variant (icon + text) for any action whose
     * inline form is icon-only, the way the mapped-icon path builds one from `iconDefaults.text`.
     */
    overflowContent?: ReactNode;
    popoverContent?: never;
    inMenu?: never;
    inMenuMdBreakpoint?: never;
    /**
     * When true, this action renders in a far-left cluster (always visible,
     * never collapses into the overflow menu) instead of with the other actions
     * on the right. Use for panel window chrome such as close/expand.
     * @default false
     */
    leading?: boolean;
    /**
     * If true, header icon will be hidden.
     * @default false
     */
    isHidden?: boolean;
} & TBTItemHeaderIconVisibility;

/**
 * Configuration for a single action in BTItemHeader.
 * Use `type: "mappedIcon"` for standard icon actions, or `type: "customComponent"` for custom rendered actions.
 */
export type TBTItemHeaderIcon = TBTItemHeaderMappedIcon | TBTItemHeaderCustomComponent;

/** Props for the BTItemHeaderIcon defaults. */
export interface IBTItemHeaderIconDefaults {
    ariaLabel: string;
    testid: string;
    icon: ReactNode;
    text: string;
}

/** Props for the BTItemHeader component. */
export type TBTItemHeaderProps = Pick<
    IBuiHeaderProps,
    "ariaLabel" | "children" | "className" | "testid"
>;
